import { Box, useDisclosure } from "@chakra-ui/react"
import TimeSeries, { DataPoint, TimeSeriesProps } from "./TimeSeries"
import HoverPopover from "@hydrologiq/shared/chakra/custom/HoverPopover"
import { useEffect, useRef, useState, useCallback } from "react"
import { toKey } from "@hydrologiq/common/test/toKey"
import { createUUID } from "@hydrologiq/common/helpers/uuid"
import { RangeResolution, rangeInMS } from "./rangeFilter"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"

interface TSWrapperProps {
  unit?: EnergyUnit
  interval?: number
  intervalResolution?: RangeResolution
  onLoaded?: () => void
  groupLabels?: string[]
}

function TSWrapper(props: TimeSeriesProps & TSWrapperProps) {
  const disclosure = useDisclosure()
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([])
  const [coords, setCoords] = useState<[number, number] | undefined>()
  const [graphId, _] = useState<string>(createUUID())
  const [showCurrentTime, setShowCurrentTime] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const scrollDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const coordsRef = useRef<[number, number] | undefined>(undefined)

  const getXY = useCallback((points: DataPoint[]): [number, number] | undefined => {
    if (points.length > 0) {
      const dataPoint0 = points[0]

      // Primary: Find graph point DOM element and use its position
      const element = (document.querySelector("#section")?.shadowRoot || window.document).querySelector(
        `.point-${toKey(`${graphId}-${dataPoint0.time.unixUtcMillis()}-${dataPoint0.group}`)}`
      )

      if (element) {
        // Try to find the actual point marker (could be element or previousElementSibling)
        const pointElement = element.previousElementSibling || element
        const rect = pointElement.getBoundingClientRect()
        // Use center of the point marker, positioned at bottom (near x-axis)
        return [rect.left + rect.width / 2, rect.top + rect.height]
      }

      // Fallback: Use pageX/pageY if DOM element not found
      if (dataPoint0.pageX !== undefined && dataPoint0.pageY !== undefined) {
        return [dataPoint0.pageX, dataPoint0.pageY]
      }
    }
    return undefined
  }, [graphId])

  // Only update coords state if changed significantly (>5px)
  const updateCoordsIfChanged = useCallback((newCoords: [number, number] | undefined) => {
    if (!newCoords) {
      coordsRef.current = undefined
      setCoords(undefined)
      return
    }

    const current = coordsRef.current
    if (!current ||
      Math.abs(current[0] - newCoords[0]) > 5 ||
      Math.abs(current[1] - newCoords[1]) > 5) {
      coordsRef.current = newCoords
      setCoords(newCoords)
    }
  }, [])

  const onDataUpdate = (points: DataPoint[]) => {
    if (points.length === 0) {
      // Close if no points
      if (disclosure.open) {
        disclosure.onClose()
      }
      setDataPoints([])
      setShowCurrentTime(false)
      coordsRef.current = undefined
      setCoords(undefined)
      return
    }

    // Check if this is a new point (different time) - if so, always update coordinates
    const isNewPoint = dataPoints.length === 0 ||
      (points.length > 0 && dataPoints.length > 0 &&
        points[0].time.unixUtcMillis() !== dataPoints[0].time.unixUtcMillis())

    // Always update dataPoints first
    setDataPoints(points)

    // Calculate coordinates
    let newCoords = getXY(points)

    // If DOM lookup failed, use pageX/pageY as fallback
    if (!newCoords && points[0].pageX !== undefined && points[0].pageY !== undefined) {
      newCoords = [points[0].pageX, points[0].pageY]
    }

    // Update coordinates if valid
    // Always update for new points, use threshold for same point to prevent micro-movements
    // HoverPopover will always re-render when coordinates change to update position
    if (newCoords && !(newCoords[0] === 0 && newCoords[1] === 0)) {
      if (isNewPoint) {
        // New point - always update coordinates
        coordsRef.current = newCoords
        setCoords(newCoords)
      } else {
        // Same point - use threshold to prevent micro-movements
        updateCoordsIfChanged(newCoords)
      }
      setShowCurrentTime(true)
    }

    // Clear scroll debounce if we have valid data (user is hovering)
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current)
      scrollDebounceRef.current = null
    }

    // Open popover if not already open
    if (!disclosure.open) {
      disclosure.onOpen()
    }
  }

  const closeOnScroll = useCallback(() => {
    if (disclosure.open) {
      disclosure.onClose()
    }
    setShowCurrentTime(false)

    // Set debounce to prevent immediate reopening
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current)
    }

    scrollDebounceRef.current = setTimeout(() => {
      scrollDebounceRef.current = null
    }, 150)
  }, [disclosure])

  const closeOnMouseLeave = useCallback(() => {
    if (disclosure.open) {
      disclosure.onClose()
    }
    setShowCurrentTime(false)
    // No debounce - allow immediate reopening on hover
  }, [disclosure])

  // Handle clicks outside graph and popover to close popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {

      const target = e.target as HTMLElement
      // Check if click is outside both graph container and popover
      if (ref.current && !ref.current.contains(target)) {
        // Check if click is on popover content
        const popoverContent = document.querySelector('[role="dialog"], [data-part="content"]')
        if (!popoverContent || !popoverContent.contains(target)) {
          // Click is outside both graph and popover, close it
          if (disclosure.open) {
            disclosure.onClose()
            setShowCurrentTime(false)
          }
        }
      }
    }

    if (disclosure.open) {
      document.addEventListener("mousedown", handleClickOutside, true)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside, true)
      }
    }
  }, [disclosure.open, disclosure])

  // Comprehensive scroll and resize event listeners
  useEffect(() => {
    const handleScroll = () => closeOnScroll()
    const handleResize = () => closeOnScroll()

    // Window events
    window.addEventListener("scroll", handleScroll, true)
    window.addEventListener("resize", handleResize, true)

    // Document scroll events
    document.addEventListener("scroll", handleScroll, true)

    // Container and parent scroll events
    const container = ref.current
    const parentElements: HTMLElement[] = []
    let parent = container?.parentElement
    while (parent) {
      parentElements.push(parent)
      parent.addEventListener("scroll", handleScroll, true)
      parent = parent.parentElement
    }

    // Also listen on container itself if it exists
    if (container) {
      container.addEventListener("scroll", handleScroll, true)
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true)
      window.removeEventListener("resize", handleResize, true)
      document.removeEventListener("scroll", handleScroll, true)
      parentElements.forEach((el) => el.removeEventListener("scroll", handleScroll, true))
      if (container) {
        container.removeEventListener("scroll", handleScroll, true)
      }
      // Cleanup timeout on unmount
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current)
      }
    }
  }, [closeOnScroll])

  let startOffsetMS: undefined | number = undefined
  if (props.interval !== undefined && props.intervalResolution !== undefined)
    startOffsetMS = rangeInMS(props.interval, props.intervalResolution)

  return (
    <Box
      className={`${showCurrentTime ? "show" : "hide"}-current-time`}
      onBlur={closeOnMouseLeave}
      onMouseLeave={closeOnMouseLeave}
      ref={ref}
      tabIndex={-1}
    >
      {disclosure.open && coords && (
        <HoverPopover
          x={coords[0]}
          y={coords[1]}
          disclosure={disclosure}
          dataPoints={dataPoints}
          startOffsetMS={startOffsetMS}
          unit={props.unit}
          container={ref}
          groupLabels={props.groupLabels}
        />
      )}
      <TimeSeries
        {...props}
        onDataPoint={onDataUpdate}
        onMouseLeave={closeOnMouseLeave}
        graphId={graphId}
        intervalMS={startOffsetMS ? startOffsetMS * 1.5 : undefined}
        onLoaded={props.onLoaded}
      />
    </Box>
  )
}

export default TSWrapper
